import React, { useState, useEffect } from 'react'
import moment from 'moment';
import { useStyle } from "./Style";
import Grid from "@material-ui/core/Grid";
import MenuItem from '@mui/material/MenuItem';
import { TextValidator, ValidatorForm, SelectValidator, } from 'react-material-ui-form-validator';

const AccidentalClaimsDetails = (props) => {
  const classes = useStyle();
  
  // State variables for accidental claim details
  const [dateOfDisability, setDateOfDisability] = useState(props.claimState.date_of_disability || props.eventDate || '');
  const [typeOfDisability, setTypeOfDisability] = useState(props.claimState.type_of_disability || '');
  const [attendingPhysician, setAttendingPhysician] = useState(props.claimState.attending_physician || '');
  const [patientAge, setPatientAge] = useState('');

  // Calculate patient age based on date of birth and event date
  const calculateAge = () => {
    if (props.member && props.member.date_of_birth && props.eventDate) {
      const dob = props.member.date_of_birth;
      const eventDate = props.eventDate;
      const age = moment(eventDate).diff(moment(dob), 'years');
      return age;
    }
    return '';
  };

  // Set patient age on component mount and when dependencies change
  useEffect(() => {
    const age = calculateAge();
    setPatientAge(age);
    if (age) {
      props.setAdditionalState("patient_age", age);
    }
  }, [props.member, props.eventDate]);

  // Set policy start date on component mount
  useEffect(() => {
    if (props.eventData && props.eventData.start_date) {
      props.setAdditionalState("policy_start_date", props.eventData.start_date);
    }
  }, [props.eventData]);

  // Add custom validation rules
  useEffect(() => {
    // Validate that disability date is not in the future
    ValidatorForm.addValidationRule('notFutureDate', (value) => {
      if (!value) return true;
      return moment(value).isSameOrBefore(moment(), 'day');
    });

    // Validate that disability date is not before birth date
    ValidatorForm.addValidationRule('afterBirthDate', (value) => {
      if (!value || !props.member || !props.member.date_of_birth) return true;
      return moment(value).isAfter(moment(props.member.date_of_birth));
    });

    return () => {
      ValidatorForm.removeValidationRule('notFutureDate');
      ValidatorForm.removeValidationRule('afterBirthDate');
    };
  }, [props.member]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    
    if (name === "dateOfDisability") {
      setDateOfDisability(value);
      props.setAdditionalState("date_of_disability", value);
    }
    if (name === "typeOfDisability") {
      setTypeOfDisability(value);
      props.setAdditionalState("type_of_disability", value);
    }
    if (name === "attendingPhysician") {
      setAttendingPhysician(value);
      props.setAdditionalState("attending_physician", value);
    }
  };

  // Get date constraints for disability date
  const getDateConstraints = () => {
    const today = moment().format('YYYY-MM-DD');
    const birthDate = props.member && props.member.date_of_birth 
      ? moment(props.member.date_of_birth).format('YYYY-MM-DD') 
      : null;
    
    return {
      min: birthDate || '1900-01-01',
      max: today
    };
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <div className={classes.headingclaim}>
            <h4>Accidental Disability Claim Information</h4>
          </div>
        </Grid>
      </Grid>
      
      <ValidatorForm onSubmit={() => {}}>
        {/* Patient Details Section */}
        <Grid container spacing={2} className={classes.selectpolicyWrapper}>
          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="firstName"
              style={{ width: "100%" }}
              label="first_name"
              name="firstName"
              value={props.member ? props.member.first_name : ''}
              disabled
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="lastName"
              style={{ width: "100%" }}
              label="last_name"
              name="lastName"
              value={props.member ? props.member.last_name : ''}
              disabled
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="relationship"
              style={{ width: "100%" }}
              label="relationship"
              name="relationship"
              value={props.member ? props.member.relationship : ''}
              disabled
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="gender"
              style={{ width: "100%" }}
              label="gender"
              name="gender"
              value={props.member ? props.member.gender : ''}
              disabled
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="dateOfBirth"
              style={{ width: "100%" }}
              label="date_of_birth"
              name="dateOfBirth"
              value={props.member ? props.member.date_of_birth : ''}
              disabled
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={4} sm={4}>
            <TextValidator
              id="patientAge"
              style={{ width: "100%" }}
              label="Age of Patient"
              name="patientAge"
              value={patientAge}
              disabled
              variant="outlined"
            />
          </Grid>
        </Grid>

        <hr />

        {/* Disability Details Section */}
        <Grid container spacing={2} className={classes.selectpolicyWrapper}>
          <Grid item xs={12} sm={4} md={4}>
            <TextValidator
              id="dateOfDisability"
              label="Date of Disability"
              name="dateOfDisability"
              value={dateOfDisability}
              onChange={handleChange}
              fullWidth
              type="date"
              inputProps={{
                min: getDateConstraints().min,
                max: getDateConstraints().max,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              validators={['required', 'notFutureDate', 'afterBirthDate']}
              errorMessages={[
                'Please enter the date of disability',
                'Disability date cannot be in the future',
                'Disability date must be after birth date'
              ]}
              style={{ width: "100%" }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <SelectValidator
              labelId="type-of-disability"
              id="type-of-disability"
              value={typeOfDisability}
              name="typeOfDisability"
              type="text"
              label="Type of Disability"
              onChange={handleChange}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please select the type of disability']}
              style={{ width: "100%" }}
              sx={{
                fontFamily: "Open Sans",
                fontStyle: "normal",
                fontWeight: 300,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.005em",
                color: "#000000",
              }}
            >
              {["Permanent Disability", "Partial Disability"].map((type, index) => (
                <MenuItem
                  key={index}
                  sx={{
                    fontFamily: "Open Sans",
                    fontStyle: "normal",
                    fontWeight: 300,
                    fontSize: "12px",
                    lineHeight: "16px",
                    letterSpacing: "0.005em",
                    color: "#000000",
                  }}
                  value={type}
                >
                  {type}
                </MenuItem>
              ))}
            </SelectValidator>
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <TextValidator
              id="attendingPhysician"
              style={{ width: "100%" }}
              label="Attending Physician"
              name="attendingPhysician"
              value={attendingPhysician}
              onChange={handleChange}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please enter the attending physician name']}
            />
          </Grid>
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default AccidentalClaimsDetails;